import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { differenceInDays, isWithinInterval, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  MapPin,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Maximize,
  Truck,
  Calendar as CalendarIcon,
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  Eye,
  Clock,
  ShieldCheck,
  CreditCard,
  Users,
  Star,
  Printer,
  TrendingUp,
  Phone,
  Send,
  Loader2,
  Bell,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import WhatsAppButton from '../components/WhatsAppButton';
import SEO, { breadcrumbSchema } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1617144078443-95018ade4ee9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
  'https://images.unsplash.com/photo-1632491744165-8683d103f1f2?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
  'https://images.unsplash.com/photo-1650211575712-86ba987a814b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=800',
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.1 } }),
};

const LocationDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();

  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [error, setError] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [showInquiry, setShowInquiry] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    subject: '', message: '', desired_start_date: '', desired_end_date: '',
    guest_name: '', guest_email: '', guest_phone: '', guest_company: '',
  });

  useEffect(() => { fetchLocation(); }, [id]);

  const fetchLocation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/locations/${id}`);
      if (!response.ok) throw new Error('Location not found');
      setLocation(await response.json());
    } catch (e) {
      setError('Standort nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async () => {
    if (!dateRange.from || !dateRange.to) return;
    setCheckingAvailability(true);
    setAvailability(null);
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(
        `${API_URL}/api/locations/${id}/availability?start_date=${dateRange.from.toISOString()}&end_date=${dateRange.to.toISOString()}`,
        { headers }
      );
      setAvailability(await res.json());
    } catch (e) {
      console.error('Error checking availability:', e);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleBooking = () => {
    if (!availability?.available) return;
    // Login required for booking
    if (!isAuthenticated) {
      toast.error('Bitte melden Sie sich an, um eine Buchung vorzunehmen.');
      navigate(`/auth?redirect=${encodeURIComponent(`/locations/${id}`)}`);
      return;
    }
    navigate('/checkout/new', {
      state: {
        locationId: id,
        startDate: dateRange.from.toISOString(),
        endDate: dateRange.to.toISOString(),
        price: availability.total_price || availability.price,
        locationName: location.name,
      },
    });
  };

  const submitInquiry = async () => {
    if (!inquiryForm.subject || !inquiryForm.message) {
      toast.error('Bitte Betreff und Nachricht ausfüllen.');
      return;
    }
    if (!isAuthenticated && (!inquiryForm.guest_name || !inquiryForm.guest_email)) {
      toast.error('Bitte Name und E-Mail angeben.');
      return;
    }
    setInquiryLoading(true);
    try {
      const url = isAuthenticated ? `${API_URL}/api/inquiries` : `${API_URL}/api/inquiries/guest`;
      const headers = { 'Content-Type': 'application/json' };
      if (isAuthenticated) headers['Authorization'] = `Bearer ${token}`;
      const body = {
        location_id: id,
        subject: inquiryForm.subject || `Anfrage: ${location?.name}`,
        message: inquiryForm.message,
        desired_start_date: inquiryForm.desired_start_date || null,
        desired_end_date: inquiryForm.desired_end_date || null,
      };
      if (!isAuthenticated) {
        body.guest_name = inquiryForm.guest_name;
        body.guest_email = inquiryForm.guest_email;
        body.guest_phone = inquiryForm.guest_phone;
        body.guest_company = inquiryForm.guest_company;
      }
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) {
        toast.success('Anfrage erfolgreich gesendet! Wir melden uns bei Ihnen.');
        setShowInquiry(false);
        setInquiryForm({ subject: '', message: '', desired_start_date: '', desired_end_date: '', guest_name: '', guest_email: '', guest_phone: '', guest_company: '' });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler beim Senden der Anfrage');
      }
    } catch (e) { toast.error('Netzwerkfehler'); }
    setInquiryLoading(false);
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);

  const isDateBooked = (date) => {
    if (!location?.booked_dates) return false;
    return location.booked_dates.some((b) => {
      const start = parseISO(b.start_date);
      const end = parseISO(b.end_date);
      return isWithinInterval(date, { start, end });
    });
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--destructive))]" />
          <h2 className="text-xl font-semibold mb-2">Fehler</h2>
          <p className="text-[hsl(var(--muted-foreground))] mb-4">{error}</p>
          <Link to="/locations"><Button>Zurück zu Standorten</Button></Link>
        </div>
      </div>
    );
  }

  const days = dateRange.from && dateRange.to ? differenceInDays(dateRange.to, dateRange.from) : 0;
  const images = location.images?.length > 0 ? location.images : FALLBACK_IMAGES;
  const trailer = location.trailer;
  const hasDimensions = trailer?.print_width_mm || trailer?.print_height_mm;

  return (
    <div className="py-6 lg:py-10" data-testid="location-detail-page">
      <SEO
        title={`Werbefläche ${location.name}`}
        description={`Werbefläche am Standort ${location.name} in ${location.address} mieten. ${location.traffic_frequency || 'Hohe'} Passantenfrequenz. Ab CHF ${location.price_per_week || 350}/Woche.`}
        canonicalPath={`/locations/${id}`}
        image={images[0]}
        type="product"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          'name': `Werbefläche ${location.name}`,
          'description': `Mobile Werbefläche am Standort ${location.name}, ${location.address}. ${location.traffic_frequency} Passantenfrequenz.`,
          'image': images[0],
          'offers': {
            '@type': 'Offer',
            'priceCurrency': 'CHF',
            'price': location.price_per_week,
            'priceSpecification': { '@type': 'UnitPriceSpecification', 'price': location.price_per_week, 'priceCurrency': 'CHF', 'unitText': 'Woche' },
            'availability': 'https://schema.org/InStock',
          },
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Link
          to="/locations"
          className="inline-flex items-center text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-5 transition-colors"
          data-testid="back-to-locations"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Alle Standorte
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ═══ LEFT COLUMN ═══ */}
          <div className="lg:col-span-2 space-y-8">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              {/* Image Gallery */}
              <div className="relative rounded-xl overflow-hidden mb-3 card-shadow group">
                <img
                  src={images[activeImg] || images[0]}
                  alt={`${location.name} – Werbestandort`}
                  className="w-full h-[280px] sm:h-[400px] object-cover"
                  data-testid="location-main-image"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setActiveImg((p) => (p - 1 + images.length) % images.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                      aria-label="Vorheriges Bild"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setActiveImg((p) => (p + 1) % images.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                      aria-label="Nächstes Bild"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
                <Badge className="absolute top-3 right-3 bg-[hsl(var(--chart-2))] text-white border-0">
                  Verfügbar
                </Badge>
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
                  {images.slice(0, 5).map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImg(idx)}
                      className={`rounded-lg overflow-hidden h-16 w-24 flex-shrink-0 border-2 transition-colors ${
                        idx === activeImg ? 'border-[hsl(var(--primary))]' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt={`${location.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Title + Address + Badges */}
              <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-semibold mb-2" data-testid="location-title">{location.name}</h1>
                <p className="text-[hsl(var(--muted-foreground))] flex items-center text-sm mb-3">
                  <MapPin className="w-4 h-4 mr-1.5 flex-shrink-0" />
                  {location.address}
                </p>
                <div className="flex flex-wrap gap-2">
                  {location.traffic_frequency && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> {location.traffic_frequency}
                    </Badge>
                  )}
                  {trailer?.has_lighting && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Lightbulb className="w-3 h-3" /> LED-Beleuchtung
                    </Badge>
                  )}
                  {trailer?.is_mobile && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Truck className="w-3 h-3" /> Mobil
                    </Badge>
                  )}
                </div>
              </div>

              {location.description && (
                <p className="text-[hsl(var(--muted-foreground))] leading-relaxed mb-6">{location.description}</p>
              )}
            </motion.div>

            {/* ═══ WARUM DIESER STANDORT? ═══ */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} data-testid="location-benefits">
              <h2 className="text-xl font-semibold mb-4">Warum dieser Standort?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    icon: Eye,
                    title: 'Hohe Sichtbarkeit',
                    desc: location.traffic_frequency
                      ? `Mit ${location.traffic_frequency} ist dieser Standort einer der meistfrequentierten in der Region.`
                      : 'Strategisch platziert an einer stark frequentierten Strasse im Zürcher Oberland.',
                  },
                  {
                    icon: Clock,
                    title: '24/7 Präsenz',
                    desc: 'Ihre Werbung wirkt rund um die Uhr – auch nachts, an Wochenenden und Feiertagen. Ohne Pause.',
                  },
                  {
                    icon: Users,
                    title: 'Lokale Zielgruppe',
                    desc: 'Erreichen Sie genau die Menschen, die in dieser Region leben, arbeiten und einkaufen.',
                  },
                  {
                    icon: ShieldCheck,
                    title: 'Exklusiver Platz',
                    desc: 'Keine andere Werbung direkt daneben. Ihre Botschaft erhält 100% der Aufmerksamkeit.',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-xl bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))]"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[hsl(var(--primary)/0.1)] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4.5 h-4.5 text-[hsl(var(--primary))]" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{item.title}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* ═══ TRAILER INFO ═══ */}
            {trailer && (
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <Card data-testid="trailer-info-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center text-lg">
                      <Truck className="w-5 h-5 mr-2" />
                      Ihr Werbeanhänger
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="p-3 rounded-lg bg-[hsl(var(--surface-2))] text-center">
                        <Maximize className="w-5 h-5 mx-auto mb-1 text-[hsl(var(--primary))]" />
                        <p className="text-lg font-semibold">{trailer.size_m2} m²</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">Werbefläche</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[hsl(var(--surface-2))] text-center">
                        <Lightbulb className="w-5 h-5 mx-auto mb-1 text-[hsl(var(--primary))]" />
                        <p className="text-lg font-semibold">{trailer.has_lighting ? 'Ja' : 'Nein'}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">LED-Beleuchtung</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[hsl(var(--surface-2))] text-center">
                        <Truck className="w-5 h-5 mx-auto mb-1 text-[hsl(var(--primary))]" />
                        <p className="text-lg font-semibold">{trailer.is_mobile ? 'Ja' : 'Fest'}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">Mobil</p>
                      </div>
                      {hasDimensions && (
                        <div className="p-3 rounded-lg bg-[hsl(var(--surface-2))] text-center">
                          <Printer className="w-5 h-5 mx-auto mb-1 text-[hsl(var(--primary))]" />
                          <p className="text-sm font-semibold">{trailer.print_width_mm}x{trailer.print_height_mm}mm</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">Druckmasse</p>
                        </div>
                      )}
                    </div>
                    {trailer.eyelet_count > 0 && (
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">
                        {trailer.eyelet_count} Ösen (Abstand: {trailer.eyelet_spacing_mm}mm)
                        {trailer.dimension_notes ? ` – ${trailer.dimension_notes}` : ''}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* ═══ WAS SIE BEKOMMEN ═══ */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} data-testid="location-what-you-get">
              <h2 className="text-xl font-semibold mb-4">Was Sie bekommen</h2>
              <div className="space-y-3">
                {[
                  'Exklusiver Werbeplatz – keine Konkurrenz direkt daneben',
                  'Professioneller Werbeanhänger, aufgestellt und betreut',
                  location.traffic_frequency ? `Standort mit ${location.traffic_frequency}` : 'Standort an stark frequentierter Strasse',
                  trailer?.has_lighting ? 'LED-Beleuchtung für Sichtbarkeit auch nach Einbruch der Dunkelheit' : 'Sichtbarkeit an einem Hauptverkehrsweg',
                  'Optionaler Druckservice – wir drucken Ihr Plakat',
                  'Flexible Laufzeiten ab 1 Woche',
                  'Sichere Online-Buchung und -Zahlung',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-[hsl(var(--chart-2))] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[hsl(var(--foreground))]">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* ═══ GUT ZU WISSEN ═══ */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} data-testid="location-faq">
              <h2 className="text-xl font-semibold mb-4">Gut zu wissen</h2>
              <div className="space-y-3">
                {[
                  { q: 'Kann ich mein eigenes Plakat mitbringen?', a: hasDimensions ? `Ja! Die Druckmasse für diesen Anhänger sind ${trailer.print_width_mm}x${trailer.print_height_mm}mm. Alternativ nutzen Sie unseren Druckservice.` : 'Ja! Sie können Ihren eigenen Druck mitbringen oder unseren Druckservice nutzen. Die genauen Druckmasse erhalten Sie nach der Buchung.' },
                  { q: 'Wie schnell ist meine Werbung sichtbar?', a: 'Nach Buchungsbestätigung stellen wir den Anhänger in der Regel innerhalb von 1-2 Werktagen auf.' },
                  { q: 'Kann ich den Zeitraum verlängern?', a: 'Ja, solange der Standort verfügbar ist, können Sie Ihre Buchung unkompliziert verlängern.' },
                  { q: 'Was passiert bei schlechtem Wetter?', a: 'Unsere Werbeanhänger sind wetterfest. Ihre Werbung ist bei Regen, Schnee und Sonne sichtbar.' },
                ].map((item, i) => (
                  <details
                    key={i}
                    className="group bg-white rounded-xl border border-[hsl(var(--border))] card-shadow"
                  >
                    <summary className="flex items-center justify-between p-4 cursor-pointer list-none font-medium text-sm hover:text-[hsl(var(--primary))] transition-colors">
                      {item.q}
                      <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] transition-transform duration-200 group-open:rotate-90 flex-shrink-0 ml-3" />
                    </summary>
                    <div className="px-4 pb-4 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                      {item.a}
                    </div>
                  </details>
                ))}
              </div>
            </motion.div>
          </div>

          {/* ═══ RIGHT SIDEBAR ═══ */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Card className="sticky top-24 card-shadow" data-testid="booking-sidebar">
                <CardContent className="p-5 space-y-5">
                  {/* Price header */}
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-2xl font-semibold">CHF {formatPrice(location.price_per_week)}</span>
                      <span className="text-sm text-[hsl(var(--muted-foreground))]">/ Woche</span>
                    </div>
                    <div className="flex items-baseline justify-between text-sm text-[hsl(var(--muted-foreground))]">
                      <span>Monatspreis</span>
                      <span className="font-medium text-[hsl(var(--foreground))]">CHF {formatPrice(location.price_per_month)}</span>
                    </div>
                  </div>

                  <div className="border-t border-[hsl(var(--border))]" />

                  {/* Calendar */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      <CalendarIcon className="w-4 h-4 inline mr-1" />
                      Wunschzeitraum wählen
                    </label>
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => { setDateRange(range || { from: null, to: null }); setAvailability(null); }}
                      numberOfMonths={1}
                      disabled={(date) => date < new Date() || isDateBooked(date)}
                      locale={de}
                      className="rounded-md border"
                      data-testid="availability-calendar"
                    />
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))]" data-testid="availability-legend">
                    <span className="flex items-center">
                      <span className="w-3 h-3 bg-[hsl(var(--primary))] rounded mr-1" /> Ausgewählt
                    </span>
                    <span className="flex items-center">
                      <span className="w-3 h-3 bg-[hsl(var(--muted))] rounded mr-1" /> Gebucht
                    </span>
                  </div>

                  {/* Check Availability */}
                  {dateRange.from && dateRange.to && !availability && (
                    <Button
                      onClick={checkAvailability}
                      className="w-full"
                      disabled={checkingAvailability}
                      data-testid="check-availability-button"
                    >
                      {checkingAvailability ? 'Wird geprüft...' : 'Verfügbarkeit prüfen'}
                    </Button>
                  )}

                  {/* Availability Result */}
                  {availability && (
                    <div>
                      {availability.available ? (
                        <Alert className="bg-[hsl(var(--chart-2)/0.1)] border-[hsl(var(--chart-2))]">
                          <CheckCircle className="h-4 w-4 text-[hsl(var(--chart-2))]" />
                          <AlertDescription>
                            <div className="space-y-1.5">
                              <p className="font-medium text-[hsl(var(--chart-2))]">Verfügbar!</p>
                              <div className="flex justify-between text-sm">
                                <span>{days} Tage ({Math.round((days / 7) * 10) / 10} Wochen)</span>
                              </div>
                              {/* Price breakdown */}
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span>Grundpreis</span>
                                  <span>CHF {formatPrice(availability.base_price || availability.price)}</span>
                                </div>
                                {availability.discount_percent > 0 && (
                                  <div className="flex justify-between text-[hsl(var(--chart-2))]">
                                    <span>ZO-Rabatt ({availability.discount_percent}%)</span>
                                    <span>- CHF {formatPrice(availability.discount_amount)}</span>
                                  </div>
                                )}
                                {availability.express_surcharge > 0 && (
                                  <div className="flex justify-between text-amber-600">
                                    <span>Express-Zuschlag</span>
                                    <span>+ CHF {formatPrice(availability.express_surcharge)}</span>
                                  </div>
                                )}
                                {availability.setup_fee > 0 && (
                                  <div className="flex justify-between text-amber-600">
                                    <span>Setup-Gebühr</span>
                                    <span>+ CHF {formatPrice(availability.setup_fee)}</span>
                                  </div>
                                )}
                                {availability.services_price > 0 && (
                                  <div className="flex justify-between">
                                    <span>Zusatzleistungen</span>
                                    <span>+ CHF {formatPrice(availability.services_price)}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex justify-between text-lg font-semibold pt-1 border-t border-[hsl(var(--chart-2)/0.2)]">
                                <span>Gesamtpreis</span>
                                <span>CHF {formatPrice(availability.total_price || availability.price)}</span>
                              </div>
                              {availability.customer_type && availability.customer_type !== 'external' && (
                                <p className="text-xs text-[hsl(var(--chart-2))]">
                                  {availability.customer_type === 'zo_firma' ? 'ZO-Firmen Rabatt angewendet' : 'Vereins-Konditionen angewendet'}
                                </p>
                              )}
                              {availability.has_existing_print && (
                                <p className="text-xs text-[hsl(var(--chart-2))]">
                                  Druck vorhanden — kein Express-Zuschlag
                                </p>
                              )}
                            </div>
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            {availability.reason || 'Ausgewählte Daten nicht verfügbar'}
                            {availability.min_lead_days && (
                              <span className="block text-xs mt-1 opacity-80">
                                Mindestvorlauf: {availability.min_lead_days} Tage
                              </span>
                            )}
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}

                  {/* Book Now */}
                  {location?.effective_available === false ? (
                    <WaitlistBlock location={location} />
                  ) : (
                    <Button
                      onClick={handleBooking}
                      className="w-full"
                      size="lg"
                      disabled={!availability?.available}
                      data-testid="location-detail-book-now"
                    >
                      {!isAuthenticated ? 'Anmelden & buchen' : 'Jetzt buchen'}
                    </Button>
                  )}

                  {/* Inquiry Button */}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setInquiryForm(prev => ({
                        ...prev,
                        subject: `Anfrage: ${location?.name || 'Werbefläche'}`,
                        desired_start_date: dateRange.from ? dateRange.from.toISOString().split('T')[0] : '',
                        desired_end_date: dateRange.to ? dateRange.to.toISOString().split('T')[0] : '',
                      }));
                      setShowInquiry(true);
                    }}
                    data-testid="location-detail-inquiry-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Unverbindliche Anfrage
                  </Button>

                  {/* Trust indicators */}
                  <div className="space-y-2 pt-2 border-t border-[hsl(var(--border))]">
                    {[
                      { icon: ShieldCheck, label: 'Sichere Zahlung via Stripe' },
                      { icon: CreditCard, label: 'Keine versteckten Kosten' },
                      { icon: Clock, label: 'Kostenlose Stornierung (gemäss AGB)' },
                      { icon: Phone, label: 'Persönliche Beratung bei Fragen' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                        <item.icon className="w-3.5 h-3.5 text-[hsl(var(--chart-2))] flex-shrink-0" />
                        {item.label}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* ═══ BOTTOM CTA ═══ */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="mt-16 p-8 bg-[hsl(var(--primary)/0.04)] border border-[hsl(var(--primary)/0.15)] rounded-2xl text-center"
          data-testid="location-bottom-cta"
        >
          <h3 className="text-xl font-semibold mb-2">Nicht der richtige Standort?</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4 max-w-lg mx-auto">
            Wir haben über 13 Standorte im Zürcher Oberland. Finden Sie den perfekten Platz für Ihre Werbung.
          </p>
          <Link to="/locations">
            <Button variant="outline" data-testid="browse-other-locations">
              Alle Standorte ansehen
            </Button>
          </Link>
        </motion.div>

        {/* ═══ INQUIRY DIALOG ═══ */}
        <Dialog open={showInquiry} onOpenChange={setShowInquiry}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Unverbindliche Anfrage senden</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {!isAuthenticated && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Name *</Label>
                      <Input data-testid="inquiry-guest-name" value={inquiryForm.guest_name} onChange={e => setInquiryForm(p => ({...p, guest_name: e.target.value}))} placeholder="Ihr Name" />
                    </div>
                    <div>
                      <Label>E-Mail *</Label>
                      <Input data-testid="inquiry-guest-email" type="email" value={inquiryForm.guest_email} onChange={e => setInquiryForm(p => ({...p, guest_email: e.target.value}))} placeholder="ihre@email.ch" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Telefon</Label>
                      <Input data-testid="inquiry-guest-phone" value={inquiryForm.guest_phone} onChange={e => setInquiryForm(p => ({...p, guest_phone: e.target.value}))} placeholder="+41 79..." />
                    </div>
                    <div>
                      <Label>Firma</Label>
                      <Input data-testid="inquiry-guest-company" value={inquiryForm.guest_company} onChange={e => setInquiryForm(p => ({...p, guest_company: e.target.value}))} placeholder="Firma (optional)" />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label>Betreff *</Label>
                <Input data-testid="inquiry-subject-loc" value={inquiryForm.subject} onChange={e => setInquiryForm(p => ({...p, subject: e.target.value}))} />
              </div>
              <div>
                <Label>Ihre Nachricht *</Label>
                <Textarea data-testid="inquiry-message-loc" value={inquiryForm.message} onChange={e => setInquiryForm(p => ({...p, message: e.target.value}))} placeholder="Beschreiben Sie Ihr Anliegen, z.B. Wunschzeitraum, besondere Anforderungen..." rows={4} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Wunsch-Start</Label>
                  <Input data-testid="inquiry-start-loc" type="date" value={inquiryForm.desired_start_date} onChange={e => setInquiryForm(p => ({...p, desired_start_date: e.target.value}))} />
                </div>
                <div>
                  <Label>Wunsch-Ende</Label>
                  <Input data-testid="inquiry-end-loc" type="date" value={inquiryForm.desired_end_date} onChange={e => setInquiryForm(p => ({...p, desired_end_date: e.target.value}))} />
                </div>
              </div>
              <Button data-testid="submit-inquiry-loc" onClick={submitInquiry} disabled={inquiryLoading} className="w-full">
                {inquiryLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wird gesendet...</> : <><Send className="w-4 h-4 mr-2" />Anfrage senden</>}
              </Button>
              <p className="text-xs text-center text-[hsl(var(--muted-foreground))]">Kostenlos und unverbindlich. Wir melden uns innerhalb von 24h.</p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

/* ─── Waitlist Block (shown when location is unavailable) ─── */
const WaitlistBlock = ({ location }) => {
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState(null); // null | 'loading' | 'success' | 'already' | 'error'

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    setWaitlistStatus('loading');
    try {
      const response = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: waitlistEmail, location_id: location.id, name: waitlistName })
      });
      const data = await response.json();
      if (response.ok) {
        setWaitlistStatus(data.already_registered ? 'already' : 'success');
      } else {
        setWaitlistStatus('error');
      }
    } catch {
      setWaitlistStatus('error');
    }
  };

  return (
    <div className="space-y-3" data-testid="location-unavailable-notice">
      <Alert className="bg-[hsl(var(--muted))] border-[hsl(var(--border))]">
        <AlertCircle className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
        <AlertDescription className="text-[hsl(var(--muted-foreground))] font-medium">
          Dieser Standort ist aktuell nicht verfügbar.
          {location.unavailable_until && (
            <span className="block text-sm mt-1">
              Voraussichtlich wieder verfügbar ab {new Date(location.unavailable_until).toLocaleDateString('de-CH')}
            </span>
          )}
        </AlertDescription>
      </Alert>
      <Button className="w-full" size="lg" disabled data-testid="location-detail-book-now">
        Aktuell nicht verfügbar
      </Button>

      {/* Waitlist Form */}
      {waitlistStatus === 'success' || waitlistStatus === 'already' ? (
        <div className="p-4 rounded-lg bg-[hsl(var(--chart-2)/0.1)] border border-[hsl(var(--chart-2)/0.3)]" data-testid="waitlist-success">
          <div className="flex items-center gap-2 text-[hsl(var(--chart-2))]">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium text-sm">
              {waitlistStatus === 'already'
                ? 'Sie sind bereits auf der Warteliste.'
                : 'Sie werden benachrichtigt, sobald dieser Standort wieder verfügbar ist.'}
            </span>
          </div>
        </div>
      ) : (
        <form onSubmit={handleWaitlistSubmit} className="p-4 rounded-lg bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] space-y-3" data-testid="waitlist-form">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-4 h-4 text-[hsl(var(--primary))]" />
            <span className="text-sm font-semibold">Benachrichtigung erhalten</span>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Tragen Sie sich ein und wir informieren Sie per E-Mail, sobald dieser Standort wieder buchbar ist.
          </p>
          <Input
            type="text"
            placeholder="Ihr Name (optional)"
            value={waitlistName}
            onChange={(e) => setWaitlistName(e.target.value)}
            data-testid="waitlist-name-input"
          />
          <Input
            type="email"
            placeholder="Ihre E-Mail-Adresse *"
            value={waitlistEmail}
            onChange={(e) => setWaitlistEmail(e.target.value)}
            required
            data-testid="waitlist-email-input"
          />
          <Button
            type="submit"
            className="w-full"
            variant="secondary"
            disabled={waitlistStatus === 'loading'}
            data-testid="waitlist-submit-button"
          >
            {waitlistStatus === 'loading' ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Benachrichtigen, wenn verfügbar
          </Button>
          {waitlistStatus === 'error' && (
            <p className="text-xs text-[hsl(var(--destructive))]">Fehler. Bitte versuchen Sie es erneut.</p>
          )}
        </form>
      )}
    </div>
  );
};


export default LocationDetailPage;
